import VimeoPlayer from '@vimeo/player';

type IPlayer = VimeoPlayer | YT.Player;

const cacheYoutubePlayers: { [key: string]: YT.Player } = {}

export default class Player {
  private player: IPlayer;
  public isReady: boolean;
  private iframe: HTMLIFrameElement;

  constructor(
    iframe: HTMLIFrameElement,
    options?: {
      onReady?: (player: Player) => void;
    }
  ) {
    const self = this
    this.isReady = false
    this.iframe = iframe
    if (iframe.dataset.provider === 'youtube') {
      if (cacheYoutubePlayers[iframe.id]) {
        this.player = cacheYoutubePlayers[iframe.id]
        this.isReady = true
        options?.onReady?.(self);
      } else {
        this.player = new YT.Player(iframe, {
          events: {
            onReady: (event) => {
              this.isReady = true
              options?.onReady?.(self);
              cacheYoutubePlayers[iframe.id] = event.target
            },
          },
        });
      }
    } else {
      this.player = new VimeoPlayer(iframe);
      this.player.ready().then(() => {
        this.isReady = true;
        options?.onReady?.(self);
      });
    }
  }

  play(): Promise<void> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.play()
    } else {
      try {
        this.player.playVideo();
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    }
  }

  pause(): Promise<void> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.pause()
    } else {
      this.player.pauseVideo();
      return Promise.resolve();
    }
  }

  stop(): Promise<void> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.unload()
    } else {
      this.player.stopVideo();
      return Promise.resolve();
    }
  }

  getCurrentTime(): Promise<number> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.getCurrentTime()
    } else {
      return Promise.resolve(this.player.getCurrentTime());
    }
  }

  setCurrentTime(seconds: number): Promise<number> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.setCurrentTime(seconds)
    } else {
      this.player.seekTo(seconds, true);
      return Promise.resolve(seconds);
    }
  }

  setMuted(muted: boolean): Promise<boolean> {
    if (this.player instanceof VimeoPlayer) {
      return this.player.setMuted(muted)
    } else {
      muted ? this.player.mute() : this.player.unMute();
      return Promise.resolve(muted);
    }
  }

  getVideoId(): Promise<string | number> {
    if (this.player instanceof VimeoPlayer) {
      return Promise.resolve(this.player.getVideoId());
    } else {
      return Promise.resolve(String(this.iframe.dataset.videoid));
    }
  }

  on(event: 'play' | 'pause' | 'ended' | 'timeupdate', callback: (data?: any) => void) {
    if (this.player instanceof VimeoPlayer) {
      this.player.on(event, callback);
    } else {
      this.player.addEventListener('onStateChange', (e: YT.OnStateChangeEvent) => {
        if (event === 'play' && e.data === YT.PlayerState.PLAYING) {
          callback(e);
        }
        if (event === 'pause' && e.data === YT.PlayerState.PAUSED) {
          callback(e);
        }
        if (event === 'ended' && e.data === YT.PlayerState.ENDED) {
          callback(e);
        }
      });
    }
  }
}
